// Local UI state, reset whenever the server's `pending` changes.

import type { CardView, Pending, TargetRef } from "../api/types";

export interface Selection {
  /** Card currently shown in the right-side preview panel. */
  inspected: CardView | null;
  /** Hand slot the inspected card came from (for play/discard). */
  handIdx: number | null;
  /** True when the inspected card was opened by an off-limits combat
   *  click. The preview still shows it, but its green ring is
   *  suppressed so it doesn't read as a valid selection. */
  inspectRingMuted: boolean;
  /** Entity ids the user has tagged as attackers this turn. */
  attackers: Set<number>;
  /** attacker entity id → set of blocker entity ids assigned to it. */
  blocks: Map<number, Set<number>>;
  /** While building a block, the blocker the user clicked first. */
  pendingBlocker: number | null;
  /** Keys via `targetKey(ref)` so entity + player picks share one Set. */
  targetPicks: Set<string>;
}

export function emptySelection(): Selection {
  return {
    inspected: null,
    handIdx: null,
    inspectRingMuted: false,
    attackers: new Set(),
    blocks: new Map(),
    pendingBlocker: null,
    targetPicks: new Set(),
  };
}

/** Drop the local attacker declaration, leaving the rest of the
 *  selection untouched. Called when the combat phase ends: the picks
 *  belonged to that combat, so they must not survive on
 *  `selection.attackers`. Left in place they linger until the next
 *  pending settles — a whole opponent turn away against an AI — and,
 *  because the replayed `pending` is still a stale `attackers`(viewer)
 *  and `combat` reads null again once combat is over, the
 *  selection-driven red ring + forward lift relit on every creature we
 *  attacked with and stayed lit through the opponent's entire turn.
 *  Returns the same reference when already empty so React skips the
 *  re-render. */
export function clearAttackerSelection(s: Selection): Selection {
  return s.attackers.size === 0 ? s : { ...s, attackers: new Set() };
}

export function targetKey(ref: TargetRef): string {
  return `${ref.kind}:${ref.id}`;
}

export function parseTargetKey(key: string): TargetRef {
  const [kind, raw] = key.split(":");
  const id = Number(raw);
  if (kind === "entity") return { kind: "entity", id };
  if (kind === "player") return { kind: "player", id: id === 0 ? 0 : 1 };
  throw new Error(`bad target key: ${key}`);
}

/** Stable identity for the current pending decision. Callers use the
 *  string for "did the pending change?" — passing `turn` makes the key
 *  include the turn so the attackers / blocks / main phases of turn N
 *  and turn N+1 don't collide. Without the turn, `selection.attackers`
 *  set during turn N's attackers phase would survive into turn N+1's
 *  attackers phase, reading as "creatures auto-marked as attackers
 *  again." */
export function pendingKey(p: Pending | null, turn?: number): string {
  if (!p) return "none";
  const t = turn != null ? `:${turn}` : "";
  // Keys back-to-back yields as different so picks reset between them.
  if (p.kind === "target") {
    const sig = p.request.candidates.map((c) => `${c.kind}:${c.id}`).join(",");
    return `target:${p.player}:${p.request.prompt ?? ""}:${p.request.min}-${p.request.max}:${sig}${t}`;
  }
  if (p.kind === "choice") {
    const sig = p.request.options.map((o) => o.key).join(",");
    return `choice:${p.player}:${p.request.prompt ?? ""}:${p.request.min}-${p.request.max}:${sig}${t}`;
  }
  return `${p.kind}:${p.player}${t}`;
}

export type FlatBlock = { attacker: number; blockers: number[] };

export function flattenBlocks(blocks: Map<number, Set<number>>): FlatBlock[] {
  const out: FlatBlock[] = [];
  for (const [attacker, bs] of blocks) {
    if (bs.size === 0) continue;
    out.push({ attacker, blockers: Array.from(bs) });
  }
  return out;
}
